import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Mail, Phone, FileText, Download } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const AdminApplications = () => {
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [appsRes, jobsRes] = await Promise.all([
        axios.get(`${API}/job-applications`),
        axios.get(`${API}/jobs`),
      ]);
      setApplications(appsRes.data);
      setJobs(jobsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/job-applications/${id}/status?status=${status}`);
      toast.success('Durum güncellendi!');
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Bir hata oluştu!');
    }
  };

  const getJobTitle = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    return job ? job.title_tr : 'Bilinmiyor';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'reviewed':
        return 'bg-blue-100 text-blue-700';
      case 'accepted':
        return 'bg-green-100 text-green-700';
      case 'rejected':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'Beklemede';
      case 'reviewed':
        return 'İncelendi';
      case 'accepted':
        return 'Kabul Edildi';
      case 'rejected':
        return 'Reddedildi';
      default:
        return status;
    }
  };

  return (
    <AdminLayout>
      <div>
        <h1 
          className="text-3xl font-bold text-gray-900 mb-8"
          style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          data-testid="applications-title"
        >
          İş Başvuruları
        </h1>

        <div className="space-y-4">
          {applications.map((app) => (
            <div 
              key={app.id} 
              className="bg-white rounded-xl border border-gray-200 p-6"
              data-testid={`application-card-${app.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{app.name}</h3>
                  <p className="text-sm text-gray-600 mb-2">{getJobTitle(app.job_id)}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      <span>{app.email}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      <span>{app.phone}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500 mb-2">
                    {format(new Date(app.created_at), 'dd MMM yyyy HH:mm', { locale: tr })}
                  </p>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(app.status)}`}>
                    {getStatusLabel(app.status)}
                  </span>
                </div>
              </div>

              {app.cv_url && (
                <div className="mb-4">
                  <a
                    href={app.cv_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                  >
                    <Download className="w-4 h-4" />
                    CV İndir
                  </a>
                </div>
              )}

              {app.cover_letter && (
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Başvuru Mektubu
                  </h4>
                  <p className="text-gray-700 text-sm whitespace-pre-wrap">{app.cover_letter}</p>
                </div>
              )}

              <div className="flex gap-2">
                <Select value={app.status} onValueChange={(value) => updateStatus(app.id, value)}>
                  <SelectTrigger className="w-40" data-testid={`application-status-${app.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Beklemede</SelectItem>
                    <SelectItem value="reviewed">İncelendi</SelectItem>
                    <SelectItem value="accepted">Kabul Et</SelectItem>
                    <SelectItem value="rejected">Reddet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminApplications;